"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { isStaleBundleError, shouldAutoReloadStaleBundle } from "@/lib/errors/stale-bundle";

/**
 * DOM-desync errors (insertBefore/removeChild NotFoundError) happen when
 * something OUTSIDE React mutates the DOM — Chrome auto-translate wrapping
 * text nodes in <font>, extensions, force-dark — and React's next commit
 * hits a stale node reference. They are recoverable: remounting the subtree
 * rebuilds a clean DOM. We auto-reset() once per session before showing the
 * crash UI.
 */
function isDomDesyncError(error: Error): boolean {
  return (
    error.name === "NotFoundError" ||
    /insertBefore|removeChild|The node before which/.test(error.message)
  );
}

/** Detects machine translation (Chrome/Edge) — the dominant cause of
 *  DOM-desync crashes. Tagging errors with this separates third-party DOM
 *  mutation from real app bugs in PostHog/Discord. */
function detectTranslated(): boolean {
  if (typeof document === "undefined") return false;
  const html = document.documentElement;
  return (
    html.classList.contains("translated-ltr") ||
    html.classList.contains("translated-rtl") ||
    // Edge translator stamps _msttexthash attributes
    document.querySelector("[_msttexthash]") !== null ||
    // Google Translate replaces text nodes with <font> wrappers
    document.querySelector("font[class*='im-translated'], font > font") !== null
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();
  const isEn = pathname?.startsWith("/en/") || pathname === "/en";

  useEffect(() => {
    if (typeof window !== "undefined" && typeof console !== "undefined") {
      console.error("Global error boundary caught:", error);
    }

    const translated = detectTranslated();
    let autoRetried = false;
    let autoReloaded = false;
    // Stale bundle (post-deploy chunk skew): the modules in memory are broken,
    // so reset() can't help — only a full reload fetches fresh HTML + chunks.
    // Hard-reload ONCE per session (guarded), after reporting below.
    if (isStaleBundleError(error) && typeof window !== "undefined") {
      // Garde TEMPOREL (1 reload / 2 min) et plus "1 / session" : on déploie
      // plusieurs fois par jour, un onglet peut subir plusieurs chunk-skews.
      autoReloaded = shouldAutoReloadStaleBundle();
    } else if (isDomDesyncError(error) && typeof window !== "undefined") {
      try {
        const key = "velyq-dom-desync-retried";
        if (sessionStorage.getItem(key) !== "1") {
          sessionStorage.setItem(key, "1");
          autoRetried = true;
        }
      } catch {
        /* sessionStorage unavailable — no retry */
      }
    }

    try {
      posthog.captureException(error, {
        digest: error.digest,
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        boundary: "root",
        translated,
        auto_retried: autoRetried,
        auto_reloaded: autoReloaded,
      });
    } catch {
      /* posthog not initialized — fail soft */
    }
    // Fire-and-forget Discord notification for real-time visibility.
    // Server-side route handler rate-limits + fails soft if webhook absent.
    // PAS de ping quand l'auto-reload va guérir (chunk-skew post-déploiement =
    // bruit attendu, prod 2026-07-11 ×3) : PostHog garde chaque occurrence, et
    // si le reload ne guérit pas, la récidive (<2 min) arrive ici avec
    // autoReloaded=false → Discord + crash UI. Un vrai bug reste visible.
    if (typeof window !== "undefined" && !autoReloaded) {
      void fetch("/api/log/error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          digest: error.digest,
          message: error.message,
          stack: error.stack,
          url: window.location.href,
          boundary: "root",
          translated,
          auto_retried: autoRetried,
          auto_reloaded: autoReloaded,
        }),
        keepalive: true,
      }).catch(() => {});
    }

    // Act AFTER reporting so the occurrence is never lost. A stale bundle needs a
    // full reload (fresh HTML + chunks); a DOM-desync just needs a remount.
    if (autoReloaded) {
      window.location.reload();
    } else if (autoRetried) {
      reset();
    }
  }, [error, reset]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <Link href={isEn ? "/en" : "/"} aria-label="Velyq" className="mb-6">
        <Logo size="lg" />
      </Link>
      <p className="text-sm font-semibold uppercase tracking-widest text-destructive">
        {isEn ? "Error" : "Erreur"}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">
        {isEn ? "Something went wrong" : "Quelque chose s'est mal passé"}
      </h1>
      <p className="mt-3 max-w-md text-muted-foreground">
        {isEn
          ? "Our team has been notified. You can retry or head back home in the meantime."
          : "Nos équipes ont été notifiées. Tu peux réessayer ou revenir à l'accueil en attendant."}
      </p>
      {error.digest && (
        <p className="mt-2 text-xs text-muted-foreground">
          Code: <span className="font-mono">{error.digest}</span>
        </p>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button size="lg" onClick={() => reset()}>
          {isEn ? "Retry" : "Réessayer"}
        </Button>
        <Button asChild size="lg" variant="outline">
          <Link href={isEn ? "/en" : "/"}>
            {isEn ? "Back to home" : "Retour à l'accueil"}
          </Link>
        </Button>
      </div>
    </div>
  );
}
